import { readChatSessionIdentity } from 'src/auth/sessionEvents';
// React Query cache-key tree for chat v2 (Epic #393 / Sprint 4 / issue #419).
//
// Keeping all keys in one file is intentional: the event router in #418
// invalidates by key, and a tiny typed builder catches drift between the
// producer (hook) and the consumer (router) at compile time. Hooks and the
// router both import from here.

export const chatKeys = {
  messageCenter: () => ['message-center', readChatSessionIdentity()] as const,
  announcementHistory: () => ['announcement-history', readChatSessionIdentity()] as const,
  /** Inbox root. */
  myChatsRoot: () => ['my-chats', readChatSessionIdentity()] as const,
  /** Inbox with filter, e.g. `{ type: 'JOB_BROADCAST' }`. */
  myChats: (filter?: { type?: string; jobId?: number; archived?: boolean }) =>
    ['my-chats', readChatSessionIdentity(), filter ?? {}] as const,
  jobChats: (jobId: number | string) => ['job-chats', readChatSessionIdentity(), jobId] as const,
  /**
   * Broadcast preview key — issue #449. Per-job, per-target,
   * per-custom-user-ids triple so toggling the target in the composer
   * doesn't reuse a stale `recipientCount`. The `customUserIds` array is
   * sorted-and-joined so the cache hit is order-independent.
   */
  broadcastPreview: (
    jobId: number | string,
    target: string,
    customUserIds?: ReadonlyArray<number>
  ) =>
    [
      'broadcast-preview',
      readChatSessionIdentity(),
      jobId,
      target,
      Array.isArray(customUserIds) && customUserIds.length > 0
        ? [...customUserIds].sort((a, b) => a - b).join(',')
        : '',
    ] as const,
  chat: (chatId: number | string) => ['chat', readChatSessionIdentity(), chatId] as const,
  /** Worker-event threads for the hub, side panel and worker profile (#4005). */
  eventThreadsRoot: () => ['event-threads', readChatSessionIdentity()] as const,
  eventThreads: (filter?: { eventId?: number; workerId?: number }) =>
    ['event-threads', readChatSessionIdentity(), filter ?? {}] as const,
  chatTopics: (chatId: number | string) => ['chat-topics', readChatSessionIdentity(), chatId] as const,
  topicPreview: (chatId: number | string, type: string, id?: number) =>
    ['chat-topic-preview', readChatSessionIdentity(), chatId, type, id ?? 0] as const,
  chatMessages: (chatId: number | string) =>
    ['chat-messages', readChatSessionIdentity(), chatId] as const,
  supportInbox: (
    status?: string,
    scope: 'legacy' | 'platform' = 'legacy'
  ) => ['support-inbox', readChatSessionIdentity(), scope, status ?? 'all'] as const,
  supportInboxRoot: () => ['support-inbox', readChatSessionIdentity()] as const,
};
